import { Modal, CustomButton } from '@ui-kit'
import { ListingTable } from '../listing-table/ListingTable'
import { DuplicatePopupModalProps } from '../../utils/types'
import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  attachCaseById,
  patchAttachForHcpEntity,
  fetchTypeStatusForCommunications,
  fetchDocumentIdForHcpEntity,
  fetchCaseDocumentIdForHcpEntity,
  addToaster,
  hideLoader,
  showLoader,
} from '@app-store'
import {
  COMM_TEMPLATE,
  DUPLICATE_POPUP,
  NAMESPACES,
} from '../../utils/constants'
import { v4 as uuid } from 'uuid'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import {
  fetchEntityIdByName,
  fetchLayoutIdByEntityId,
} from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'
import { caseCancelOrVoidApi } from '../../apis/caseStatus.api'
import {
  editEntityData,
  editEntityDataEmbeddedAPI,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { FieldValues } from 'react-hook-form'

export const DuplicatePopupModal = ({
  isOpen,
  title = '',
  totalResults = 0,
  dataFetchCallback,
  entityId,
  entityName,
  layout,
  extraColumns = [],
  extraActions = [],
  onProceedAnyway,
  onCancel,
  isLoading = false,
  updateStatusToProcessing,
  communicationId,
  faxDetails,
  intakeEntityName,
  fromInteraction,
}: DuplicatePopupModalProps) => {
  const dispatch = useAppDispatch()
  const authUser = useAppSelector((state) => state.authUser)
  const categoryData = useAppSelector(
    (state) => state?.categoryDropdown?.categoryMap
  )
  const getCaseDocumentTypeId = () =>
    categoryData?.[COMM_TEMPLATE.CASE_DOCUMENT_TYPE]?.find(
      (item) => item.value === DUPLICATE_POPUP.INTAKE_FORM
    )?.id

  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowInstanceId = workflowInstance?.id
  const caseId = workflowInstance?.case_id
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const [layoutIdForHcpEntity, setLayoutIdForHcpEntity] = useState<number>()

  const fetchWebEnrollmentStatusId = async (): Promise<string | undefined> => {
    const response = await fetchTypeStatusForCommunications(
      NAMESPACES.WEB_ENROLLMENT_STATUS
    )
    return response?.data?.find(
      (item: { value: string }) =>
        item?.value?.toLowerCase() === NAMESPACES?.PROCESSING_LOWERCASE
    )?.id
  }

  const fetchCaseDocumentId = async (
    id: number | string
  ): Promise<string | undefined> => {
    const entityData = await fetchEntityIdByName(
      dispatch,
      NAMESPACES?.WEB_ENROLLMENT_DOCUMENT
    )
    if (!entityData) return

    const documents = await fetchDocumentIdForHcpEntity(
      entityData?.id.toString() ?? '',
      faxDetails?.enrollment_id ?? ''
    )

    const matchedDoc = documents?.data?.content?.find(
      (item: { enrollment_id: string }) =>
        item?.enrollment_id === faxDetails?.enrollment_id
    )

    if (!matchedDoc?.document?.id) return

    const caseDocEntity = await fetchEntityIdByName(
      dispatch,
      NAMESPACES?.CASE_DOCUMENTS
    )
    if (!caseDocEntity) return

    const layout = await fetchLayoutIdByEntityId(
      dispatch,
      caseDocEntity.id,
      QUEUE_STRINGS?.EDIT_MODE
    )
    const layoutId = layout?.find(
      (item: { entity_name: string }) =>
        item?.entity_name === NAMESPACES.CASE_DOCUMENTS
    )?.id

    if (!layoutId) return

    const caseDocumentTypeId = getCaseDocumentTypeId()
    if (!caseDocumentTypeId) return

    const caseDocument = await fetchCaseDocumentIdForHcpEntity(
      caseDocEntity.id.toString(),
      layoutId.toString(),
      id,
      matchedDoc.document.id.toString(),
      faxDetails?.enrollment_id,
      caseDocumentTypeId
    )

    return caseDocument?.data?.id
  }

  const handleHcpEntityAttachment = async (id: string | number) => {
    const statusId = await fetchWebEnrollmentStatusId()
    if (!statusId) return

    const attached = await patchAttachForHcpEntity(
      [communicationId ?? ''],
      entityId.toString() ?? '',
      layoutIdForHcpEntity?.toString() ?? '',
      id,
      statusId
    )

    if (!attached) return

    showSuccessToast()

    await fetchCaseDocumentId(id)
  }

  const handleDefaultEntityAttachment = async (
    id: string | number,
    reason: string,
    reviewer: string
  ) => {
    const res = await attachCaseById({
      faxId: communicationId ?? '',
      caseId: id,
      reason,
      reviewer,
    })

    showSuccessToast()

    const caseDocEntity = await fetchEntityIdByName(
      dispatch,
      NAMESPACES?.CASE_DOCUMENTS
    )
    if (!caseDocEntity) return

    const layout = await fetchLayoutIdByEntityId(
      dispatch,
      caseDocEntity.id,
      QUEUE_STRINGS?.EDIT_MODE
    )
    const layoutId = layout?.find(
      (item: { entity_name: string }) =>
        item?.entity_name === NAMESPACES?.CASE_DOCUMENTS
    )?.id

    if (!layoutId) return

    const caseDocumentTypeId = getCaseDocumentTypeId()
    if (!caseDocumentTypeId) return

    await editEntityData(
      NAMESPACES?.CASE_DOCUMENTS,
      caseDocEntity.id.toString(),
      layoutId.toString(),
      {
        case_document_type: {
          id: caseDocumentTypeId,
          _state: NAMESPACES?.CHANGED,
        },
      },
      res?.data?.document_entities?.id,
      []
    )
  }

  const showSuccessToast = () => {
    dispatch(
      addToaster({
        message: QUEUE_STRINGS?.CASE_ATTACHED,
        type: 'success',
        id: uuid(),
      })
    )
  }

  const setCaseCompletionDateApi = async () => {
    try {
      await editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(caseId),
        { case_completion_date: new Date().toISOString() },
        ['case_completion_date']
      )
    } catch (error) {
      console.error(error)
    }
  }

  const closeCurrentCaseAfterAttach = async () => {
    try {
      dispatch(showLoader())
      await caseCancelOrVoidApi(workflowInstanceId?.toString(), STRINGS.CANCEL)
      const response = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['case_completion_date'],
        Number(caseId)
      )
      if (!response?.data?.case_completion_date) {
        await setCaseCompletionDateApi()
      }
    } catch (error) {
      console.error(error)
      dispatch(
        addToaster({
          message: STRINGS?.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const onSubmitAttach = async (id: string | number) => {
    try {
      dispatch(showLoader())
      const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
      if (intakeEntityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
        await handleHcpEntityAttachment(id)
      } else {
        await handleDefaultEntityAttachment(id, NAMESPACES?.NO_REASON, reviewer)
      }
      await updateStatusToProcessing?.(Number(id))
    } catch (err) {
      console.error(err)
    } finally {
      await closeCurrentCaseAfterAttach()
      window.open(
        `/entity/32/workflow/review/${id}?tabs=Correspondence`,
        '_self'
      )
      dispatch(hideLoader())
    }
  }

  const handleAttachClick = (row: FieldValues) => {
    onSubmitAttach(row?.id)
  }

  useEffect(() => {
    const fetchLayoutIdForHcpEntity = async () => {
      const response = await fetchLayoutIdByEntityId(
        dispatch,
        entityId,
        QUEUE_STRINGS?.EDIT_MODE
      )
      const layoutIdForHcpEntity = response.find(
        (item: { entity_name: string }) =>
          item.entity_name === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
      )?.id
      layoutIdForHcpEntity && setLayoutIdForHcpEntity(layoutIdForHcpEntity)
    }
    if (
      entityId &&
      intakeEntityName === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
    ) {
      fetchLayoutIdForHcpEntity()
    }
  }, [entityId])

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onCancel}
      width="min-w-[60vw] max-w-[70vw]"
      showCloseButton={false}
      closeModalOnOutsideClick={true}
      isDragable
    >
      <div className="bg-accent-0 flex flex-col h-full">
        <div className="flex justify-between items-center pb-2">
          <div className="flex items-baseline gap-2">
            <div className="text-sm font-primary font-medium">{title}</div>
            <div className="text-xs font-primary font-light">
              {totalResults} {DUPLICATE_POPUP.RESULT}
              {totalResults !== 1 && 's'} {DUPLICATE_POPUP.FOUND}
            </div>
          </div>
          <div className="flex gap-2.5">
            <CustomButton onClick={onCancel}>
              {NAMESPACES?.CANCEL_BUTTON}
            </CustomButton>
            <CustomButton isFilled onClick={onProceedAnyway}>
              {DUPLICATE_POPUP?.PROCEED_ANYWAY}
            </CustomButton>
          </div>
        </div>
        <div className="text-xs font-primary font-light">
          {DUPLICATE_POPUP.SELECTING}{' '}
          <span className="font-medium">
            {fromInteraction ? DUPLICATE_POPUP.LINK : NAMESPACES.ATTACH}
          </span>{' '}
          {fromInteraction
            ? DUPLICATE_POPUP.LINK_SUBTITLE_MSG
            : DUPLICATE_POPUP.ATTACH_SUBTITLE_MSG}
        </div>

        <div className="mt-2.5 border rounded-md p-2 flex flex-col flex-1 min-h-0">
          <ListingTable
            entityId={entityId}
            entityName={entityName}
            layout={layout}
            dataFetchCallback={dataFetchCallback}
            extraColumns={extraColumns}
            extraActions={extraActions?.map((item) =>
              item.key === DUPLICATE_POPUP?.ATTACH_KEY
                ? {
                    ...item,
                    onClick: (row) => handleAttachClick(row),
                  }
                : item
            )}
            hideFilters={true}
            openInNewTab={true}
            isLoading={isLoading}
            isSortable={false}
          />
        </div>
      </div>
    </Modal>
  )
}
